import React, { useState, useEffect } from "react";

const slug = (name) =>
  name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { MENU_CATEGORIES as menuCategories } from "@/lib/menuData";

export default function MenuSection({ standalone, showHeader = true }) {
  const CategoryHeading = showHeader ? "h3" : "h2";
  const ItemHeading = showHeader ? "h4" : "h3";
  // A section link like /menu#pizze opens that tab; /menu#item-diavola opens its tab and scrolls to the dish
  const { hash } = useLocation();
  const fromHash = () => {
    const h = window.location.hash.slice(1);
    return menuCategories.find((c) => slug(c.name) === h || c.items.some((i) => `item-${slug(i.name)}` === h))?.name || "Antipasti";
  };
  const [activeCategory, setActiveCategory] = useState(fromHash);
  useEffect(() => {
    setActiveCategory(fromHash());
    if (!hash.startsWith("#item-")) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash.slice(1));
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("bg-primary/10");
      setTimeout(() => el.classList.remove("bg-primary/10"), 2000);
    }, 150);
    return () => clearTimeout(t);
  }, [hash]);
  const selectCategory = (name) => {
    setActiveCategory(name);
    window.history.replaceState(null, "", `#${slug(name)}`);
  };

  return (
    <section id="menu" aria-labelledby={showHeader ? "menu-heading" : undefined} aria-label={showHeader ? undefined : "Menu"} className={`${standalone ? 'py-12' : showHeader ? 'py-16 md:py-24' : 'pt-10 pb-16 md:pb-24'} px-6 bg-card`}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        {showHeader && (
          <motion.div
            initial={{ y: 30 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <p className="font-accent text-sm tracking-[0.4em] uppercase text-primary mb-3" lang="it">
              Il Nostro Menu
            </p>
            <h2 id="menu-heading" className="font-display text-4xl md:text-6xl text-foreground mb-3" lang="it">
              A' Cucina e <em className="not-italic italic text-primary">Mamma'</em>
            </h2>
            <p className="font-body text-xs tracking-[0.3em] uppercase text-primary/60 mb-2">
              Neapolitan Menu · Italian Wine Bar · New York City
            </p>
            <div className="w-16 h-px bg-primary mx-auto mt-4" />
          </motion.div>
        )}

        <p className="font-body text-xs tracking-[0.15em] uppercase text-primary text-center border-y border-primary/20 py-3 mb-8">
          20% gratuity added to parties of 5 or more · 4% discount applied to all cash payments
        </p>

        {/* Category tabs — every panel stays in the DOM; inactive ones use the hidden attribute */}
        <div role="tablist" aria-label="Menu sections" className="flex flex-wrap gap-1 mb-8 justify-center">
          {menuCategories.map((cat) => (
            <button
              key={cat.name}
              role="tab"
              id={`tab-${slug(cat.name)}`}
              aria-controls={slug(cat.name)}
              aria-selected={activeCategory === cat.name}
              onClick={() => selectCategory(cat.name)}
              className={`font-body text-[10px] tracking-[0.25em] uppercase px-5 min-h-[40px] border transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring ${
                activeCategory === cat.name
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-transparent text-muted-foreground hover:text-primary"
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {menuCategories.map((cat) => (
          <div
            key={cat.name}
            id={slug(cat.name)}
            role="tabpanel"
            aria-labelledby={`tab-${slug(cat.name)}`}
            hidden={activeCategory !== cat.name}
          >
            <CategoryHeading className="font-display text-2xl md:text-3xl text-foreground text-center mb-6">
              {cat.name}
            </CategoryHeading>
            {cat.note && (
              <p className="text-center font-body text-xs tracking-widest uppercase text-secondary mb-8 font-medium">
                {cat.note}
              </p>
            )}
            <div className="space-y-0">
              {cat.items.map((item) => (
                <div key={item.name} id={`item-${slug(item.name)}`} style={{ scrollMarginTop: "100px" }} className="group py-5 transition-colors duration-700 border-b border-border/50 last:border-0">
                  <div className="flex justify-between items-baseline mb-1">
                    <ItemHeading className="font-display text-base md:text-lg text-foreground group-hover:text-primary transition-colors duration-200">
                      {item.name}
                    </ItemHeading>
                    <div className="flex-1 mx-4 border-b border-dotted border-border/40 mb-1" />
                    <span className="font-accent text-lg text-primary whitespace-nowrap">{item.price}</span>
                  </div>
                  {item.desc && <p className="font-body text-sm text-muted-foreground">{item.desc}</p>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}